/**
 * UnifiedScoringService.ts - Facade for the new UnifiedScoringCalculator
 * 
 * This service now acts as a backward-compatible wrapper around the new
 * UnifiedScoringCalculator. It maintains the existing API while delegating 
 * to the simplified implementation.
 */

import { Investment } from '../types/Investment';
import { IncidentResponse, Incident } from '../types/Incident';
import { 
  ScoringConfiguration 
} from '../types/ScoringConfiguration';
import { ScoringConfigurationService } from './ScoringConfigurationService';
import { UnifiedScoringCalculator } from '../scoring/UnifiedScoringCalculator';
import { SynergyMapping } from '../types/ScenarioManagement';
import { ActiveSynergy } from '../scoring/SynergyProcessor';

// Core types from calculator.ts
export interface ScoreCategory {
  security: number;
  reputation: number;
  financial: number;
  operational: number;
}

export interface ScoreBreakdown {
  baseScore: ScoreCategory;
  investmentBonus: ScoreCategory;
  incidentImpact: ScoreCategory;
  responseTimeBonus: number;
  finalScore: ScoreCategory;
  overallScore: number;
  grade: string;
  performance: string;
  investmentImpactDetails?: InvestmentImpactDetails[];
}

export interface InvestmentImpactDetails {
  investmentId: string;
  investmentName: string;
  baseMitigation: number;
  synergyBonus: number;
  totalMitigation: number;
  synergyActive: boolean;
  synergyDescription?: string;
}

// Business criteria types from businessImpactCalculations.ts
export interface BusinessCriteria {
  regulatoryCompliance: number;
  operationalResiliency: number;
  customerTrust: number;
  ipProtection: number;
  productIntegrity: number;
}

// Result type that includes both scoring approaches
export interface UnifiedScoreResult extends ScoreBreakdown {
  businessCriteria: BusinessCriteria;
  configurationName?: string;
  activeSynergies?: ActiveSynergy[];
}

/**
 * UnifiedScoringService provides all scoring functionality in one place
 */
export class UnifiedScoringService {
  private static instance: UnifiedScoringService;
  private configuration: ScoringConfiguration | null = null;
  private calculator: UnifiedScoringCalculator | null = null;

  private constructor() {
    this.loadActiveConfiguration();
  }

  public static getInstance(): UnifiedScoringService {
    if (!UnifiedScoringService.instance) {
      UnifiedScoringService.instance = new UnifiedScoringService();
    }
    return UnifiedScoringService.instance;
  }

  /**
   * Load the active scoring configuration
   */
  private async loadActiveConfiguration(): Promise<void> {
    const activeConfig = await ScoringConfigurationService.getActive();
    if (activeConfig) {
      this.configuration = activeConfig;
    }
  }

  /**
   * Set a specific configuration (for testing or switching profiles)
   */
  public setConfiguration(config: ScoringConfiguration): void {
    this.configuration = config;
    // Recreate calculator with new configuration
    this.calculator = new UnifiedScoringCalculator(config);
  }

  /**
   * Get current configuration
   */
  public getConfiguration(): ScoringConfiguration | null {
    return this.configuration;
  }

  /**
   * Main scoring calculation method - delegates to UnifiedScoringCalculator
   */
  public calculateScore(
    investments: Investment[],
    incidentResponses: IncidentResponse[],
    incidents: Incident[],
    scenarioSynergies?: SynergyMapping[]
  ): UnifiedScoreResult {
    // Ensure calculator is initialized
    if (!this.calculator) {
      const config = this.configuration || this.getDefaultConfiguration();
      this.calculator = new UnifiedScoringCalculator(config);
    }
    
    // Delegate to UnifiedScoringCalculator
    const result = this.calculator.calculateScore(investments, incidentResponses, incidents, scenarioSynergies);
    
    // Map result to backward-compatible format
    return {
      baseScore: result.baseScore,
      investmentBonus: result.investmentScore,  // Map from new naming
      incidentImpact: result.incidentPenalty,   // Map from new naming
      responseTimeBonus: 0, // Always 0 - time no longer affects scoring
      finalScore: result.finalScore,
      overallScore: result.overallScore,
      grade: result.grade,
      performance: result.performance,
      investmentImpactDetails: result.investmentImpactDetails,
      businessCriteria: result.businessCriteria,
      configurationName: this.configuration?.name,
      activeSynergies: result.activeSynergies
    };
  }

  /**
   * Calculate score for a single incident (preview mode)
   */
  public calculateIncidentPreview(
    optionId: string,
    incidentId: string,
    responseTime: number,
    investments: Investment[],
    incident: Incident,
    scenarioSynergies?: SynergyMapping[]
  ): Partial<ScoreBreakdown> {
    // Ensure calculator is initialized
    if (!this.calculator) {
      const config = this.configuration || this.getDefaultConfiguration();
      this.calculator = new UnifiedScoringCalculator(config);
    }

    // Delegate to calculator
    const result = this.calculator.calculateIncidentPreview(optionId, incident, investments, scenarioSynergies);

    return {
      incidentImpact: result.incidentPenalty || { security: 0, reputation: 0, financial: 0, operational: 0 },
      responseTimeBonus: 0, // Always 0 - time no longer affects scoring
      investmentImpactDetails: result.investmentImpactDetails
    };
  }

  /**
   * Map scoring categories to business criteria
   */
  public mapToBusinessCriteria(scores: ScoreCategory): BusinessCriteria {
    // Return the mapping based on the provided scores
    const safeScore = (score: number): number => {
      if (typeof score !== 'number' || isNaN(score) || !isFinite(score)) return 0;
      return Math.min(100, Math.max(0, score));
    };
    
    const security = safeScore(scores.security);
    const operational = safeScore(scores.operational);
    const reputation = safeScore(scores.reputation);
    const financial = safeScore(scores.financial);
    
    // Use standard mapping for backward compatibility
    return {
      regulatoryCompliance: Math.round(security * 0.4 + operational * 0.4 + reputation * 0.2),
      operationalResiliency: Math.round(operational * 0.5 + financial * 0.3 + security * 0.2),
      customerTrust: Math.round(reputation * 0.6 + security * 0.2 + operational * 0.2),
      ipProtection: Math.round(security * 0.7 + operational * 0.3),
      productIntegrity: Math.round(operational * 0.3 + security * 0.3 + reputation * 0.2 + financial * 0.2)
    };
  }

  /**
   * Calculate overall business score from criteria
   */
  public calculateBusinessScore(criteria: BusinessCriteria): number {
    const scores = [
      criteria.regulatoryCompliance,
      criteria.operationalResiliency,
      criteria.customerTrust,
      criteria.ipProtection,
      criteria.productIntegrity
    ];
    
    const validScores = scores.filter(score => 
      typeof score === 'number' && !isNaN(score) && isFinite(score)
    );
    
    if (validScores.length === 0) return 0;
    
    const sum = validScores.reduce((acc, score) => acc + score, 0);
    return Math.round(sum / validScores.length);
  }

  /**
   * Get business impact labels
   */
  public getBusinessImpactLabels(): Record<keyof BusinessCriteria, string> {
    return {
      regulatoryCompliance: 'Regulatory Compliance',
      operationalResiliency: 'Operational Resiliency',
      customerTrust: 'Customer Trust',
      ipProtection: 'IP Protection',
      productIntegrity: 'Product Integrity'
    };
  }

  /**
   * Get scoring explanation
   */
  public getScoreExplanation(result: UnifiedScoreResult): string {
    if (!this.calculator) {
      return 'Calculator not initialized';
    }
    
    // Convert UnifiedScoreResult to the format expected by calculator
    const calcResult = {
      ...result,
      investmentScore: result.investmentBonus,
      incidentPenalty: result.incidentImpact
    };
    
    return this.calculator.getScoreExplanation(calcResult as any);
  }

  /**
   * Default configuration matching the original calculator.ts
   */
  private getDefaultConfiguration(): ScoringConfiguration {
    return {
      id: 'default',
      name: 'Default Configuration',
      description: 'Standard scoring configuration',
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      
      baseScores: {
        security: 50,
        reputation: 50,
        financial: 50,
        operational: 50
      },
      
      categoryWeights: {
        security: 0.3,
        reputation: 0.2,
        financial: 0.2,
        operational: 0.3
      },
      
      maxIncidentMitigation: 0.7,
      enableSynergies: false,
      enableResponseTimeBonus: false,
      maxResponseTimeImprovement: 0,
      difficultyMultiplier: 1.0,
      version: 1,
      
      responseTimeBonus: {
        excellentThreshold: 0.25,
        excellentBonus: 0,
        goodThreshold: 0.5,
        goodBonus: 0,
        acceptableThreshold: 0.75,
        acceptableBonus: 0
      },
      
      uiThresholds: {
        mitigationCap: {
          low: 0.2,
          medium: 0.5,
          high: 0.8
        },
        performanceRatings: {
          excellent: 90,
          good: 70,
          average: 50,
          poor: 30
        },
        impactSignificance: {
          critical: 80,
          major: 60,
          moderate: 40,
          minor: 20
        },
        colorCoding: {
          positive: 60,
          neutral: 40,
          negative: 20
        }
      },
      
      recommendationTriggers: {
        lowSecurityScore: 40,
        lowOperationalScore: 40,
        lowReputationScore: 40,
        lowFinancialScore: 40,
        lackOfInvestments: 2,
        unbalancedPortfolio: 0.3,
        consistentPoorPerformance: 3,
        responseTimeIssues: 0.8
      },
      
      investmentEffects: {
        'security-training': {
          categoryBonuses: { security: 15, reputation: 5, financial: 0, operational: 5 },
          incidentMitigation: 0.1,
          responseTimeImprovement: 0.05
        },
        'incident-response-team': {
          categoryBonuses: { security: 10, reputation: 10, financial: 0, operational: 15 },
          incidentMitigation: 0.15,
          responseTimeImprovement: 0.2
        },
        'security-monitoring': {
          categoryBonuses: { security: 20, reputation: 5, financial: 0, operational: 10 },
          incidentMitigation: 0.2,
          responseTimeImprovement: 0.15
        },
        'backup-recovery': {
          categoryBonuses: { security: 5, reputation: 5, financial: 10, operational: 20 },
          incidentMitigation: 0.25,
          responseTimeImprovement: 0.1
        },
        'cyber-insurance': {
          categoryBonuses: { security: 0, reputation: 10, financial: 25, operational: 5 },
          incidentMitigation: 0.0,
          responseTimeImprovement: 0.0
        },
        'compliance-program': {
          categoryBonuses: { security: 10, reputation: 15, financial: 5, operational: 10 },
          incidentMitigation: 0.05,
          responseTimeImprovement: 0.05
        },
        'zero-trust-architecture': {
          categoryBonuses: { security: 30, reputation: 10, financial: 0, operational: 15 },
          incidentMitigation: 0.3,
          responseTimeImprovement: 0.1
        },
        'ai-threat-detection': {
          categoryBonuses: { security: 25, reputation: 5, financial: 0, operational: 20 },
          incidentMitigation: 0.25,
          responseTimeImprovement: 0.15
        },
        'data-encryption': {
          categoryBonuses: { security: 15, reputation: 10, financial: 5, operational: 5 },
          incidentMitigation: 0.1,
          responseTimeImprovement: 0.05
        },
        'crisis-response-team': {
          categoryBonuses: { security: 5, reputation: 20, financial: 5, operational: 20 },
          incidentMitigation: 0.2,
          responseTimeImprovement: 0.25
        },
        'reputation-recovery': {
          categoryBonuses: { security: 0, reputation: 30, financial: 10, operational: 10 },
          incidentMitigation: 0.05,
          responseTimeImprovement: 0.0
        },
        'legal-defense-fund': {
          categoryBonuses: { security: 0, reputation: 15, financial: 20, operational: 5 },
          incidentMitigation: 0.0,
          responseTimeImprovement: 0.0
        },
        'customer-retention-program': {
          categoryBonuses: { security: 0, reputation: 25, financial: 15, operational: 10 },
          incidentMitigation: 0.05,
          responseTimeImprovement: 0.0
        },
        'forensic-investigation': {
          categoryBonuses: { security: 15, reputation: 10, financial: 0, operational: 15 },
          incidentMitigation: 0.15,
          responseTimeImprovement: 0.1
        },
        'regulatory-compliance-accelerator': {
          categoryBonuses: { security: 5, reputation: 20, financial: 10, operational: 15 },
          incidentMitigation: 0.1,
          responseTimeImprovement: 0.05
        },
        'executive-protection': {
          categoryBonuses: { security: 10, reputation: 15, financial: 5, operational: 10 },
          incidentMitigation: 0.1,
          responseTimeImprovement: 0.05
        }
      },
      
      investmentSynergies: [],
      
      gradeThresholds: [
        { grade: 'EXCELLENT', minScore: 85, label: 'Outstanding response' },
        { grade: 'WISE', minScore: 70, label: 'Smart decision making' },
        { grade: 'DECENT', minScore: 50, label: 'Reasonable response' },
        { grade: 'OK', minScore: 30, label: 'Acceptable response' },
        { grade: 'POOR', minScore: 0, label: 'Suboptimal response' }
      ]
    };
  }
}

// Export singleton instance for backward compatibility
export const unifiedScoringService = UnifiedScoringService.getInstance();

// Export types for backward compatibility
export type { ScoreCalculationResult } from '../utils/ScoringCalculator';